<template>
    <div class="redesign-layout-switcher flex items-center gap-x-1 lg:gap-x-3" data-testid="navbar-layout-switcher">
        <div class="layout-switcher-group flex items-center">
            <el-tooltip
                effect="dark"
                :content="$t('tooltip.select.tabbed.view')"
                :open-delay="200"
                placement="top"
            >
                <button
                    data-testid="layout-button-tabbed-view"
                    :class="{'is--active': activeType === TAB_VIEW}"
                    class="inline-flex items-center justify-center p-1.5 text-color border border-ui-lines outline-none"
                    @click="switchView(TAB_VIEW)"
                >
                    <redesign-base-icon
                        name="Table2"
                        :size="22"
                    />
                </button>
            </el-tooltip>
            <el-tooltip
                effect="dark"
                :content="$t('tooltip.select.list.view')"
                :open-delay="200"
                placement="top"
            >
                <button
                    data-testid="layout-button-list-view"
                    :class="{'is--active': activeType === LIST_VIEW}"
                    class="inline-flex items-center justify-center p-1.5 text-color border border-ui-lines outline-none"
                    @click="switchView(LIST_VIEW)"
                >
                    <redesign-base-icon
                        name="List"
                        :size="22"
                    />
                </button>
            </el-tooltip>
        </div>
        <div>
            <el-tooltip
                effect="dark"
                :content="$t('dashboard.onFullScreenMode')"
                placement="top"
            >
                <button
                    data-testid="button-fullscreen"
                    class="inline-flex items-center justify-center p-1.5 text-color rounded transition hover:text-primary outline-none focus-visible:ring-1 ring-active-elements"
                    @click="onFullScreen"
                >
                    <redesign-base-icon
                        :name="isFullscreenMode ? 'Shrink' : 'Expand'"
                        :size="22"
                    />
                </button>
            </el-tooltip>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Tooltip as ElTooltip } from 'element-ui'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import { useFullscreen } from '@vueuse/core'

const TAB_VIEW = 'tabbed'
const LIST_VIEW = 'list'

defineProps<{
    activeType?: string
}>()

const emit = defineEmits([ 'switch-layout' ])

const { isFullscreen: isFullscreenMode, toggle: toggleFullscreen } = useFullscreen()

function onFullScreen () {
    toggleFullscreen()
}

function switchView (type: string) {
    emit('switch-layout', type)
}
</script>

<style lang="scss">
    .redesign-layout-switcher {
        .layout-switcher-group {
            @apply overflow-hidden;
            button {
                @apply border-l-0;
                &:first-child {
                    @apply rounded-l border-l;
                }
                &:last-child {
                    @apply rounded-r;
                }
                @apply transition overflow-hidden;
                &:hover {
                    @apply text-active-elements;
                    background-color: color-mix(in srgb, var(--active-elements) 5%, transparent);
                }
                &:focus-visible {
                    background-color: color-mix(in srgb, var(--active-elements) 15%, transparent);
                }
                &.is--active {
                    @apply bg-active-elements text-white;
                    &:focus-visible {
                        background-color: color-mix(in srgb, var(--active-elements) 75%, #000);
                    }
                }
            }
        }
    }
    [dir='rtl'], .rtl {
        .redesign-layout-switcher {
            .layout-switcher-group {
                .button {
                    &:first-child {
                        @apply rounded-l rounded-r-none border-r;
                        background-color: red !important;
                    }
                    &:last-child {
                        @apply rounded-full;
                        background-color: green !important;
                    }
                }
            }
        }
        .redesign-layout-switcher .layout-switcher-group button:first-child {
            @apply rounded-r rounded-l-none border-l-0;
        }
        .redesign-layout-switcher .layout-switcher-group button:last-child {
            @apply rounded-l rounded-r-none border-l;
        }
    }
</style>
